import { autoinject, transient } from "aurelia-dependency-injection";
import { I18N } from "aurelia-i18n";
import * as moment from "moment";
import { RightShipDialogServiceConstants } from "../../../../../../../constants/basics/rightship-dialog.constants";
import { MobileHelper } from "../../../../../../../helpers/mobile-helper";
import { DataGridColumn, DataType, ItemSource, QueryFilter, SourceType } from "../../../../../../../models/queries/queries.models";
import { UserClientService } from "../../../../../../../services/authentication/user-client.service";
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import { NotificationClientService, NotificationType } from "../../../../../../../services/basics/notification-client.service";
import { RequestClientService } from "@rightship-tech/common-ui/services/basic/request-client.service";
import { QueriesClientService } from "../../../../../../../services/queries/queries-client.services";
import { InitFilterValueConverter } from "../../../../../../components/converters/queries/init-filter";
import { BasePaginatedFilterableVesselPortList, ParamFilterModel, PortCallType, PortRiskProfileFilterModel } from "../../../models/port-risk-profile.models";
import { VesselPortCallODataModel, PortTerminalAndBerthResponse, PortCallSummaryItem } from "../../../models/ports-and-terminals.models";
import { PortsAndTerminalsService } from "../../../services/ports-and-terminals.service";
import { PortRiskProfileFilters } from "../../components/port-risk-profile-filters/port-risk-profile-filters";
import { useView } from "aurelia-framework";

@autoinject()
@transient()
@useView('views/pages/modules/ports-and-terminals/port-risk-profile/tabs/vessels-pre-arrival/vessels-pre-arrival.html')
export class VesselsPreArrival extends BasePaginatedFilterableVesselPortList<VesselPortCallODataModel>
{
    listTitle: string = 'Pre-Arrival Vessels';
    type: PortCallType = "pre-arrivals";
    filters: QueryFilter[];
    isLoading: boolean = false;
    portTerminalAndBerthResponse: PortTerminalAndBerthResponse;
    itemSource: ItemSource;
    columns: DataGridColumn[];
    public filtersVM: PortRiskProfileFilters;
    public portCallSummaryItems: PortCallSummaryItem[] = [];

    currentVm: VesselsPreArrival = this;

    constructor(i18N: I18N, queriesClientService: QueriesClientService, notificationClientService: NotificationClientService,
        initFilterConverter: InitFilterValueConverter, userClientService: UserClientService, requestClient: RequestClientService,
        portsAndTerminalsService: PortsAndTerminalsService, private dialogClientService: DialogClientService) {
        super(i18N, queriesClientService, notificationClientService, initFilterConverter, userClientService, requestClient, portsAndTerminalsService);
        this.setColumns();
    }

    async activate() {
        await this.loadFiltersData();
    }

    async applyFilters(filters: ParamFilterModel[]): Promise<void> {
        this.isLoading = true;
        this.parameterFilters = filters ? filters : [];

        try {

            const initialFilters = this.getInitialFilters();
            initialFilters.push(...this.getSearchFilter());

            this.setPreArrivalsParameterFilters();

            this.appliedFilters = initialFilters ? initialFilters : [];

            await this.reloadData();
            this.isLoading = false;
        }
        catch (e) {
            this.isLoading = false;
            this.notificationClientService.show(this.i18N.tr('error-text'), 'Failed to apply filters', NotificationType.Error, 5000)
        }
    }

    private setPreArrivalsParameterFilters() {
        let index = this.parameterFilters.findIndex(x => x.parameterName == "riskLevelIds");
        if (index > -1)
            this.parameterFilters.splice(index, 1);

        index = this.parameterFilters.findIndex(x => x.parameterName == "statusIds");
        if (index > -1)
            this.parameterFilters.splice(index, 1);

        if (this.riskLevelFilterIds && this.riskLevelFilterIds.length > 0) {
            this.parameterFilters.push({ parameterName: "riskLevelIds", dataType: DataType.Default, paramValue: this.riskLevelFilterIds.join(",") });
        }

        if (this.statusFilterIds && this.statusFilterIds.length > 0) {
            this.parameterFilters.push({ parameterName: "statusIds", dataType: DataType.Default, paramValue: this.statusFilterIds.join(",") });
        }
    }

    private getPreArrivalsUrl() {
        return `odata/VesselPortArrivals/Genesis.GetVesselPreArrivals(businessUnitId=${this.businessUnitId ? this.businessUnitId : "null"},${this.getParameterValue("portIds", true)},${this.getParameterValue("countryIds", true)},${this.getParameterValue("terminalIds", true)},${this.getParameterValue("berthIds", true)},${this.getParameterValue("etaFrom", true)},${this.getParameterValue("etaTo", true)},${this.getParameterValue("riskLevelIds", true)},${this.getParameterValue("statusIds", true)})`;
    }

    async reloadData(): Promise<void> {
        this.itemSource = {
            sourceType: SourceType.ODataUrl,
            source: this.getPreArrivalsUrl(),
            columns: this.columns,
            initialFilters: this.appliedFilters
        };

        this.portCallSummaryItems = await this.portsAndTerminalsService.getPortCallSummaryItems({
            berthIds: this.getParameterValue("berthIds", false),
            countryIds: this.getParameterValue("countryIds", false),
            etaFrom: this.getParameterValue("etaFrom", false),
            etaTo: this.getParameterValue("etaTo", false),
            portIds: this.getParameterValue("portIds", false),
            terminalIds: this.getParameterValue("terminalIds", false),
            portCallType: 'pre-arrivals'
        });
    }

    getInitialFilters = (): QueryFilter[] => [];

    private setColumns() {
        const allColumns: DataGridColumn[] = [
            <DataGridColumn>
            {
                columnOrder: 1,
                sortOrder: 1,
                dataType: DataType.CustomTemplate,
                instancePerRow: true,
                name: "vesselName",
                title: "Name",
                isSortable: true,
                value:
                    '<div class="column">' +
                    '   <a class="link" href="/vessel-pre-arrival/${item.vesselPortCallId}">${item.vesselName}</a>' +
                    '   <span class="info" if.bind="item.vesselIdentifier">${item.vesselIdentifierType} ${item.vesselIdentifier}</span>' +
                    '</div>',
                parameter: {
                    item: null
                }
            },
            <DataGridColumn>
            {
                columnOrder: 2,
                sortOrder: 2,
                dataType: DataType.CustomTemplate,
                instancePerRow: true,
                name: "overallScreeningRisk",
                title: "Overall Risk Level",
                isSortable: true,
                width: MobileHelper.isMobileDevice() && window.innerWidth < 1024 ? "" : "140px",
                value:
                    '<div class="row nowrap">' +
                    '<div class="indicator ${getRiskClass(item.overallScreeningRisk)}"></div>' +
                    '<div class="column">' +
                    '   <span class="risk-level">${item.overallScreeningRisk ? item.overallScreeningRisk: "Pending"}</span>' +
                    '   <span class="info">${formatDate(item.lastScreeningDate)}</span>' +
                    '</div>' +
                    '</div>',
                parameter: {
                    item: null,
                    getRiskClass: (riskLevel: string) => {
                        return riskLevel ? riskLevel.toLowerCase().trim().replace(" ", "-") : "pending"
                    },
                    formatDate: (date: string | Date) => {
                        return date ? moment(date).format("DD MMM YYYY, HH:mm") : "No Analysis yet";
                    }
                }
            },
            <DataGridColumn>
            {
                columnOrder: 3,
                sortOrder: 3,
                dataType: DataType.CustomTemplate,
                instancePerRow: true,
                name: "validFormNameList",
                title: "Valid Forms",
                isSortable: true,
                value:
                    '<div class="valid-forms-cell">'+
                        '<div class="column">' +
                            '<div ref="validForms" if.bind="getValidFormsArray(item).length">' +
                                '<div>${getValidFormsArray(item)[0]}</div>' +
                            '</div>' +
                            '<span class="info" if.bind="getValidFormsArray(item).length > 1">+ ${getValidFormsArray(item).length - 1}</span>' +
                        '</div>' +

                        //Tooltip
                        '<comp-tooltip class="comp-tooltip--valid-forms" on-element.bind="validForms" show-arrow="true" padding="0"'+
                            'min-width="292" show.delegate="onShow(item)">' +
                            '<div class="header" show.bind="item.forms">Valid Forms (${item.forms.length})</div>' +
                            '<div class="forms" show.bind="item.forms">' +
                                '<template repeat.for="validForm of item.forms">' +
                                    '<div class="form column">' +
                                        '<span class="name">${validForm.name}</span>' +
                                        '<span class="date">${validForm.validUntil | dateFormat: "DD MMM YYYY"}</span>' +
                                    '</div>' +
                                '</template>' +
                            '</div>' +

                            //Loading
                            '<div class="loading row align-items-middle" show.bind="!item.forms">'+
                                '<comp-loader></comp-loader>'+
                                '<span class="text">Loading forms...</span>'+
                            '</div>' +
                        '</comp-tooltip>' +

                        //Fallback
                        '<div if.bind="!getValidFormsArray(item).length">' +
                            '<span>No Valid Forms</span>' +
                        '</div>' +
                    '</div>',
                parameter: {
                    item: null,
                    onShow: (item) => {
                        this.portsAndTerminalsService.getValidVesselPortCallForms(item.vesselPortCallId, this.businessUnitId).then(list => {
                            item.forms = list;
                        });
                    },
                    forms: [],
                    getValidFormsArray: (item) => item.validFormNameList ? item.validFormNameList.split(",") : []
                }
            },
            <DataGridColumn>
            {
                columnOrder: 4,
                sortOrder: 4,
                dataType: DataType.CustomTemplate,
                instancePerRow: true,
                name: 'portName',
                title: 'Port',
                isSortable: true,
                width: MobileHelper.isMobileDevice() && window.innerWidth < 1024 ? "" : "250px",
                value:
                    '<div class="column">' +
                    '<span>${item.portName}</span>' +
                    '<span class="info">${getTerminalAndBerthText(item)}</span>' +
                    '</div>',
                parameter: {
                    item: null,
                    getTerminalAndBerthText: (item: VesselPortCallODataModel) => {
                        return item.terminalSKey == null || item.terminalSKey == -1 ? "Terminal --" : item.terminalName +
                            ", " + (item.berthSKey == null || item.berthSKey == -1 ? "Berth --" : item.berthName);
                    }
                }
            },
            <DataGridColumn>{
                columnOrder: 5,
                sortOrder: 5,
                dataType: DataType.CustomTemplate,
                instancePerRow: true,
                name: 'eta',
                title: 'Estimated Arrival',
                isSortable: true,
                isAscending: false,
                value:
                    '<span >${formatDate(item.eta)}</span>',
                parameter: {
                    item: null,
                    formatDate: (date: string | Date) => {
                        return date ? moment(date).utc().format("DD MMM YYYY HH:mm") : null;
                    }
                }
            },
            <DataGridColumn>{
                columnOrder: 6,
                sortOrder: 6,
                dataType: DataType.CustomTemplate,
                instancePerRow: true,
                name: 'reviewStatus',
                title: 'Review Status',
                isSortable: true,
                width: MobileHelper.isMobileDevice() && window.innerWidth < 1024 ? "" : "220px",
                value:
                    '<div class="row align-items-middle nowrap">' +
                    '<span>${item.reviewStatus}</span>' +
                    '<div class="review-status">' +
                    '<comp-icon class="icon-info ${item.reviewStatus == `Reviewed (with warnings)` ? `has-warnings`:``}" icon="${item.reviewStatus == `Reviewed (with warnings)` ? `info-exclamation-mark`:`info`}" if.bind="item.statusRemarks"></comp-icon>' +
                    //Tooltip
                    '<div class="review-status-tooltip" if.bind="item.statusRemarks">' +
                    '<div class="wrap-tooltip">' +
                    '<div>' +
                    '<span class="text-bold">Remarks</span>' +
                    '</div>' +
                    '<div class="desc">${item.statusRemarks}</div>' +
                    '<span class="info">${formatDate(item.lastStatusDate)}</span>' +
                    '</div>' +
                    '</div>' +
                    '</div>' +
                    '</div>' +
                    '<span class="info">${formatDate(item.lastStatusDate)}</span>',
                parameter: {
                    item: null,
                    formatDate: (date: string | Date) => {
                        return date ? moment(date).format("DD MMM YYYY HH:mm") : "No review status";
                    }
                }
            },
            {
                columnOrder: 99,
                sortOrder: 99,
                dataType: DataType.CustomTemplate,
                instancePerRow: true,
                name: "menu",
                value: `<div class="row justify-content-end align-items-middle">
                            <list-menu hide-menu-border="true" menu-items.bind="menuItems(item)" if.bind="menuItems(item).length > 0">
                                <template replace-part="menu-item">
                                    <div class="row nowrap align-items-middle">
                                        <span class="text">\${menuItem.item.text}</span>
                                    </div>  
                                </template>
                            </list-menu>
                        </div>`,
                parameter: {
                    item: null,
                    menuItems: (item) => {
                        return [

                            //Edit Port
                            {
                                isVisible: true,
                                click: () => this.dialogClientService.show(RightShipDialogServiceConstants.URLs.UpdateVesselPortDetailsDialog,
                                    {
                                        item: item, portTerminalAndBerthResponse: this.portTerminalAndBerthResponse,
                                        onSave: () => { this.reloadData(); }
                                    }, true, false, false, "right"),
                                item: { text: "Edit Port" },
                                classList: ''
                            },

                            //Edit Estimated Arrival
                            {
                                isVisible: true,
                                click: () => this.dialogClientService.show(RightShipDialogServiceConstants.URLs.UpdateVesselArrivalDepartureDialog,
                                    {
                                        item,
                                        onSave: () => { this.reloadData(); }
                                    }, true, false, false, "right"),
                                item: { text: "Edit Estimated Arrival" },
                                classList: ''
                            },

                            //Request Forms
                            {
                                isVisible: true,
                                click: () => this.dialogClientService.show(RightShipDialogServiceConstants.URLs.PortAndTerminalsRequestFormsDialog,
                                    {
                                        portSKey: item.portSKey,
                                        vesselBKey: item.vesselSKey,
                                        vesselPortCallId: item.vesselPortCallId,
                                        vesselName: item.vesselName,
                                        vesselImoNumber: item.vesselIdentifier,
                                        onSave: () => { this.reloadData(); }
                                    }, true, false, false, "right"),
                                item: { text: "Request Forms" },
                                classList: ''
                            },

                            //View All Forms
                            {
                                isVisible: true,
                                click: () => this.dialogClientService.show(RightShipDialogServiceConstants.URLs.PortCallFormsDialog,
                                    {
                                        vesselSKey: item.vesselSKey,
                                        vesselName: item.vesselName,
                                        vesselImoNumber: item.vesselIdentifier
                                    }, true, false, false, "right"),
                                item: { text: "View All Forms" },
                                classList: ''
                            }]
                    }
                }
            }
        ];

        this.columns = allColumns;
    }

    openAddVesselToScheduleDialog() {
        this.dialogClientService.show(RightShipDialogServiceConstants.URLs.AddVesselToScheduleDialog,
            {
                portTerminalAndBerthResponse: this.portTerminalAndBerthResponse, onSave: () => { this.reloadData(); }
            }, true, false, false, "right");
    }

    updateFilterData(model: PortRiskProfileFilterModel) {
        if (model) {
            this.parameterFilters = model.paramFilters;
            this.portTerminalAndBerthResponse = model.portTerminalAndBerthResponse;
            this.applyFilters(this.parameterFilters);
        }
    }

    isPassedRules(ruleResult: string): boolean {
        return ruleResult && ruleResult.toLowerCase().trim().startsWith("passed");
    }

    public async refresh(): Promise<void> {
        if (this.filtersVM)
            await this.filtersVM.reloadData();
    }

    private getExportColumns() {
        const exportColumns: DataGridColumn[] = [
            <DataGridColumn>
            {
                columnOrder: 1,
                sortOrder: 1,
                dataType: DataType.Default,
                name: "vesselName",
                title: "Vessel Name",
                includeInExcel: true
            },
            <DataGridColumn>
            {
                columnOrder: 2,
                sortOrder: 2,
                dataType: DataType.Default,
                name: "vesselIdentifier",
                title: "Vessel IMO",
                includeInExcel: true
            },
            <DataGridColumn>
            {
                columnOrder: 3,
                sortOrder: 3,
                dataType: DataType.Default,
                name: "overallScreeningRisk",
                title: "Risk Level",
                includeInExcel: true
            },
            <DataGridColumn>
            {
                columnOrder: 4,
                sortOrder: 4,
                dataType: DataType.DateTime,
                name: "lastScreeningDate",
                title: "Risk Analysis Date",
                includeInExcel: true
            },
            <DataGridColumn>
            {
                columnOrder: 5,
                sortOrder: 5,
                dataType: DataType.Default,
                name: "portName",
                title: "Port",
                includeInExcel: true
            },
            <DataGridColumn>
            {
                columnOrder: 6,
                sortOrder: 6,
                dataType: DataType.Default,
                name: "terminalName",
                title: "Terminal",
                includeInExcel: true
            },
            <DataGridColumn>
            {
                columnOrder: 7,
                sortOrder: 7,
                dataType: DataType.Default,
                name: "berthName",
                title: "Berth",
                includeInExcel: true
            },
            <DataGridColumn>{
                columnOrder: 8,
                sortOrder: 8,
                dataType: DataType.DateTime,
                name: 'eta',
                title: 'Estimated Arrival',
                isSortable: true,
                isAscending: false,
                includeInExcel: true
            },
            <DataGridColumn>{
                columnOrder: 9,
                sortOrder: 9,
                dataType: DataType.Default,
                name: 'reviewStatus',
                title: 'Review Status',
                includeInExcel: true
            },
            <DataGridColumn>{
                columnOrder: 10,
                sortOrder: 10,
                dataType: DataType.DateTime,
                name: 'lastStatusDate',
                title: 'Review Status Date',
                includeInExcel: true
            }
        ];

        return exportColumns;
    }

    async exportList() {
        let filename = "Pre Arrivals.xlsx";
        const ports = this.getParameterValue("portNames", false);
        if (ports && ports.trim() !== "") {
            filename = ports.split(",").map(x => x.trim()).join(" ") + " " + filename;
        }
        await this.downloadExcel(this.getExportColumns(), filename);
    }
}
